import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import TutorSession from '../components/TutorSession';
import NewTutorSession from '../components/NewTutorSession';
import { PlusCircle } from 'lucide-react';

export default function TutorHub() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewSessionModalOpen, setIsNewSessionModalOpen] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('tutor_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = (sessionId: string) => {
    // Implement session joining logic
    console.log('Joining session:', sessionId);
  };

  const handleCancelSession = async (sessionId: string) => {
    try {
      const { error } = await supabase
        .from('tutor_sessions')
        .update({ status: 'cancelled' })
        .eq('id', sessionId);

      if (error) throw error;
      fetchSessions();
    } catch (error) {
      console.error('Error cancelling session:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Your Tutoring Sessions</h2>
          <p className="text-gray-600 mt-1">Get personalized help with your AI learning journey</p>
        </div>
        
        <button
          onClick={() => setIsNewSessionModalOpen(true)}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <PlusCircle className="w-5 h-5" />
          New Session
        </button>
      </div>

      <div className="space-y-6">
        {sessions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No tutoring sessions yet.</p>
            <button
              onClick={() => setIsNewSessionModalOpen(true)}
              className="text-purple-600 font-medium hover:text-purple-700 mt-2"
            >
              Start your first session
            </button>
          </div>
        ) : (
          sessions.map((session) => (
            <TutorSession
              key={session.id}
              id={session.id}
              topic={session.topic}
              status={session.status}
              startTime={new Date(session.created_at).toLocaleString()}
              onJoin={() => handleJoinSession(session.id)}
              onCancel={() => handleCancelSession(session.id)}
            />
          ))
        )}
      </div>

      <NewTutorSession
        isOpen={isNewSessionModalOpen}
        onClose={() => setIsNewSessionModalOpen(false)}
        onSuccess={fetchSessions}
      />
    </div>
  );
}